// Step 5d Phase 1 — offline cache operations (write-through population + offline reads).
// Step 5d Phase 4 — encryption-at-rest. Values are stored as AES-GCM envelopes under the
// device DEK (held in memory after PIN unlock — dekSession). When LOCKED (no DEK), writes
// are skipped and reads return null: the cache is opaque without an unlock, and the unlock
// gate stands in front of the board so this is never hit during normal use.
//
// Every helper is failure-tolerant: a broken/denied IndexedDB OR a locked DEK degrades to
// "no offline cache" — it must NEVER break the online app.

import { getOfflineDb, type ScopeRecord } from './db';
import { getDek } from './dekSession';
import { encryptJson, decryptJson, type Envelope } from './crypto';
import type { Booking } from '../opsBoardTypes';

// ── Scope keys ────────────────────────────────────────────────────────────────

/** Canonical bookings scope key. The board fetches tenant-wide (no base filter) → 'all'. */
export function bookingsScopeKey(baseId: string | null, fromIso: string, toIso: string): string {
  return `bookings:${baseId ?? 'all'}:${fromIso}:${toIso}`;
}

const refScopeKey = (collection: string): string => `ref:${collection}`;

// Phase 2: the most recent bookings-scope refreshed_at the board worked from — the
// `read_stamp` an offline op carries. Tracks fetches AND offline cache reads.
let lastBookingsRefreshedAt: string | null = null;
export function getBookingsReadStamp(): string | null {
  return lastBookingsRefreshedAt;
}

function warn(op: string, e: unknown): void {
  console.warn(`[offline] ${op} failed — continuing without cache`, e);
}

// ── Encrypted scope row (keyPath 'key' store): key plaintext, the rest encrypted. ──
interface EncScopeRow { key: string; env: Envelope }
const scopePayload = (s: ScopeRecord) => ({ params: s.params, refreshed_at: s.refreshed_at, item_count: s.item_count, ids: s.ids, cursor: s.cursor });

/** Build the encrypted scope row for a `scopes` put. */
async function encScope(dek: Uint8Array, s: ScopeRecord): Promise<EncScopeRow> {
  return { key: s.key, env: await encryptJson(dek, scopePayload(s)) };
}
async function readScope(dek: Uint8Array, key: string): Promise<ScopeRecord | null> {
  const db = await getOfflineDb();
  const row = (await db.get('scopes', key)) as EncScopeRow | undefined;
  if (!row?.env) return null;
  const p = await decryptJson<Omit<ScopeRecord, 'key'>>(dek, row.env);
  return { key, ...p };
}

// ── Bookings: replace-by-scope (encrypted) ─────────────────────────────────────

// 5d Phase 3b: `cursor` is the change-feed keyset position to stamp on this scope. The
// board's online refresh captures the feed head BEFORE the fetch (capture-before-fetch) and
// passes it here so the cursor never races ahead of the data it represents. Omitted →
// preserve the previous scope's cursor (a non-feed caller must not wipe it).
export async function saveBookingsScope(
  key: string, params: Record<string, string>, bookings: Booking[], cursor?: string | null,
): Promise<void> {
  const dek = getDek();
  if (!dek) return; // locked — nothing to write through
  try {
    const db = await getOfflineDb();
    const prevScope = await readScope(dek, key);
    const newIds = bookings.map((b) => b.id);
    const newIdSet = new Set(newIds);
    const tx = db.transaction(['scopes', 'bookings'], 'readwrite');
    for (const oldId of prevScope?.ids ?? []) {
      if (!newIdSet.has(oldId)) await tx.objectStore('bookings').delete(oldId);
    }
    for (const b of bookings) await tx.objectStore('bookings').put(await encryptJson(dek, b), b.id); // out-of-line key
    const refreshed_at = new Date().toISOString();
    const nextCursor = cursor !== undefined ? cursor : (prevScope?.cursor ?? null);
    await tx.objectStore('scopes').put(await encScope(dek, { key, params, refreshed_at, item_count: bookings.length, ids: newIds, cursor: nextCursor }));
    await tx.done;
    lastBookingsRefreshedAt = refreshed_at;
  } catch (e) {
    warn(`saveBookingsScope(${key})`, e);
  }
}

// 5d Phase 3b — the change-feed cursor for a bookings scope (read/write JUST the cursor,
// encrypted like the rest). The cursor is OPAQUE here: stored + echoed to the server
// verbatim, never parsed. null until a baseline is established (or after a Phase-4 wipe).
export async function getBookingsCursor(key: string): Promise<string | null> {
  const dek = getDek();
  if (!dek) return null;
  try {
    const scope = await readScope(dek, key);
    return scope?.cursor ?? null;
  } catch (e) {
    warn(`getBookingsCursor(${key})`, e);
    return null;
  }
}

// Advance JUST the cursor for an already-cached bookings scope AND refresh its data-age
// stamp — used after a change-feed poll returns EMPTY: no payload changed, but the device
// has confirmed it is current as of now (so the banner must reflect that). Touches only the
// scope row, never the (heavy) booking rows. No-op if the scope was never cached — the
// fetch path will establish a baseline instead.
export async function setBookingsCursor(key: string, cursor: string | null): Promise<void> {
  const dek = getDek();
  if (!dek) return;
  try {
    const db = await getOfflineDb();
    const scope = await readScope(dek, key);
    if (!scope) return;
    const refreshed_at = new Date().toISOString();
    await db.put('scopes', await encScope(dek, { ...scope, cursor, refreshed_at }));
    lastBookingsRefreshedAt = refreshed_at;
  } catch (e) {
    warn(`setBookingsCursor(${key})`, e);
  }
}

export interface CachedBookingsScope { scope: ScopeRecord; bookings: Booking[] }

/** All cached booking rows (5d-p2b overlap advisory). Decrypts each; [] when locked. */
export async function readAllCachedBookings(): Promise<Booking[]> {
  const dek = getDek();
  if (!dek) return [];
  try {
    const db = await getOfflineDb();
    const envs = (await db.getAll('bookings')) as Envelope[];
    const out: Booking[] = [];
    for (const env of envs) { try { out.push(await decryptJson<Booking>(dek, env)); } catch { /* skip unreadable */ } }
    return out;
  } catch (e) {
    warn('readAllCachedBookings', e);
    return [];
  }
}

/** Offline read: the scope's bookings as last fetched, or null if never cached / locked. */
export async function readBookingsScope(key: string): Promise<CachedBookingsScope | null> {
  const dek = getDek();
  if (!dek) return null;
  try {
    const db = await getOfflineDb();
    const scope = await readScope(dek, key);
    if (!scope) return null;
    const bookings: Booking[] = [];
    for (const id of scope.ids ?? []) {
      const env = (await db.get('bookings', id)) as Envelope | undefined;
      if (env) { try { bookings.push(await decryptJson<Booking>(dek, env)); } catch { /* skip */ } }
    }
    lastBookingsRefreshedAt = scope.refreshed_at;
    return { scope, bookings };
  } catch (e) {
    warn(`readBookingsScope(${key})`, e);
    return null;
  }
}

// ── 5d Phase 5 — scope enumeration, covering-read, eviction, pinned list ─────────
// The rolling cache stores the home −2/+7d window as ONE wide bookings scope (and pinned
// ranges as more wide scopes). To render a narrow day/week view offline from a wide cached
// scope, readCoveringBookings() reconstructs the requested window by filtering the freshest
// COVERING scope's rows — exactly what GET /api/bookings?from&to returns (block_start_at in
// [from,to]). This is what makes the deliberate prefetch readable at board granularity.

const PINNED_KEY = '__pinned__'; // reserved scopes-store key (real keys are bookings:/ref:)

/** Decrypt every real scope metadata row (skips the reserved __pinned__ meta row). */
async function listScopesRaw(dek: Uint8Array): Promise<ScopeRecord[]> {
  const db = await getOfflineDb();
  const rows = (await db.getAll('scopes')) as EncScopeRow[];
  const out: ScopeRecord[] = [];
  for (const row of rows) {
    if (!row?.key || row.key.startsWith('__') || !row.env) continue;
    try { out.push({ key: row.key, ...(await decryptJson<Omit<ScopeRecord, 'key'>>(dek, row.env)) }); } catch { /* skip */ }
  }
  return out;
}

/** Age (ms) of a cached scope's refreshed_at, or null if absent/locked. Drives prefetch dedup. */
export async function scopeAgeMs(key: string): Promise<number | null> {
  const dek = getDek();
  if (!dek) return null;
  try {
    const scope = await readScope(dek, key);
    return scope ? Date.now() - new Date(scope.refreshed_at).getTime() : null;
  } catch { return null; }
}

/**
 * Offline read for an arbitrary window: exact scope first (the fast Phase-1 path), else the
 * freshest CACHED bookings scope whose [from,to] covers the request, with its rows filtered
 * to the window. Returns null when nothing covers it (honest empty state). Data-age reflects
 * the covering scope's refreshed_at.
 */
export async function readCoveringBookings(fromIso: string, toIso: string): Promise<CachedBookingsScope | null> {
  const exactKey = bookingsScopeKey(null, fromIso, toIso);
  const exact = await readBookingsScope(exactKey);
  if (exact) return exact;
  const dek = getDek();
  if (!dek) return null;
  try {
    const db = await getOfflineDb();
    const covering = (await listScopesRaw(dek))
      .filter((s) => s.key.startsWith('bookings:') && s.params.from && s.params.to && s.params.from <= fromIso && s.params.to >= toIso)
      .sort((a, b) => b.refreshed_at.localeCompare(a.refreshed_at))[0];
    if (!covering) return null;
    const bookings: Booking[] = [];
    for (const id of covering.ids ?? []) {
      const env = (await db.get('bookings', id)) as Envelope | undefined;
      if (!env) continue;
      try {
        const b = await decryptJson<Booking>(dek, env);
        if (b.block_start_at && b.block_start_at >= fromIso && b.block_start_at <= toIso) bookings.push(b); // mirror the API's block_start_at range
      } catch { /* skip */ }
    }
    lastBookingsRefreshedAt = covering.refreshed_at;
    return {
      scope: { key: exactKey, params: { from: fromIso, to: toIso }, refreshed_at: covering.refreshed_at, item_count: bookings.length, ids: bookings.map((b) => b.id), cursor: covering.cursor },
      bookings,
    };
  } catch (e) {
    warn(`readCoveringBookings(${fromIso}..${toIso})`, e);
    return null;
  }
}

/**
 * Evict a bookings scope (release path): delete the scope row + ONLY the booking rows it
 * exclusively owns. A row still referenced by ANY other scope (the rolling window, the
 * displayed window, another pin) is kept — so releasing a pin never corrupts another scope.
 */
export async function evictScope(key: string): Promise<void> {
  const dek = getDek();
  if (!dek) return;
  try {
    const db = await getOfflineDb();
    const target = await readScope(dek, key);
    if (!target) { await db.delete('scopes', key); return; }
    const keep = new Set<string>();
    for (const s of await listScopesRaw(dek)) {
      if (s.key === key || !s.key.startsWith('bookings:')) continue;
      for (const id of s.ids ?? []) keep.add(id);
    }
    const tx = db.transaction(['scopes', 'bookings'], 'readwrite');
    for (const id of target.ids ?? []) if (!keep.has(id)) await tx.objectStore('bookings').delete(id);
    await tx.objectStore('scopes').delete(key);
    await tx.done;
  } catch (e) {
    warn(`evictScope(${key})`, e);
  }
}

// ── Cache inventory (read-only truth; DEK-gated) ────────────────────────────────
export interface ScopeInventoryEntry {
  key: string;
  kind: 'bookings' | 'reference';
  item_count: number;
  refreshed_at: string;
  /** bookings scopes */ from?: string; to?: string; cursor?: string | null;
  /** reference scopes */ collection?: string;
  pinned: boolean;
}

/** Read-only inventory of every cached scope (decrypted). [] when locked — never plaintext. */
export async function listScopes(): Promise<ScopeInventoryEntry[]> {
  const dek = getDek();
  if (!dek) return [];
  try {
    const pinnedKeys = new Set((await getPinnedScopes()).map((p) => p.key));
    return (await listScopesRaw(dek)).map((s) => {
      const isBookings = s.key.startsWith('bookings:');
      return {
        key: s.key,
        kind: isBookings ? 'bookings' : 'reference',
        item_count: s.item_count,
        refreshed_at: s.refreshed_at,
        ...(isBookings ? { from: s.params.from, to: s.params.to, cursor: s.cursor } : { collection: s.params.collection }),
        pinned: pinnedKeys.has(s.key),
      } as ScopeInventoryEntry;
    });
  } catch (e) {
    warn('listScopes', e);
    return [];
  }
}

// ── Pinned scopes (manual "take offline") — encrypted, in the reserved scopes row ──
export interface PinnedScope { key: string; label: string; from: string; to: string; pinned_at: string }

export async function getPinnedScopes(): Promise<PinnedScope[]> {
  const dek = getDek();
  if (!dek) return [];
  try {
    const db = await getOfflineDb();
    const row = (await db.get('scopes', PINNED_KEY)) as EncScopeRow | undefined;
    if (!row?.env) return [];
    const p = await decryptJson<{ scopes: PinnedScope[] }>(dek, row.env);
    return p?.scopes ?? [];
  } catch (e) {
    warn('getPinnedScopes', e);
    return [];
  }
}

async function setPinnedScopes(list: PinnedScope[]): Promise<void> {
  const dek = getDek();
  if (!dek) return;
  const db = await getOfflineDb();
  await db.put('scopes', { key: PINNED_KEY, env: await encryptJson(dek, { scopes: list }) } as EncScopeRow);
}

/** Add a pin (idempotent by key). The caller has already cached the scope's rows. */
export async function pinScope(p: PinnedScope): Promise<void> {
  const list = await getPinnedScopes();
  if (list.some((x) => x.key === p.key)) return;
  await setPinnedScopes([...list, p]);
}

/** Release a pin: drop it from the list AND evict its exclusively-owned rows. */
export async function releasePinnedScope(key: string): Promise<void> {
  const list = await getPinnedScopes();
  await setPinnedScopes(list.filter((p) => p.key !== key));
  await evictScope(key);
}

// ── Reference collections: merge (encrypted) ────────────────────────────────────

export async function saveReference<T>(collection: string, items: T[], keyOf: (item: T) => string): Promise<void> {
  const dek = getDek();
  if (!dek) return;
  try {
    const db = await getOfflineDb();
    const tx = db.transaction(['reference', 'scopes'], 'readwrite');
    for (const item of items) {
      await tx.objectStore('reference').put(await encryptJson(dek, item), `${collection}:${keyOf(item)}`);
    }
    await tx.objectStore('scopes').put(await encScope(dek, { key: refScopeKey(collection), params: { collection }, refreshed_at: new Date().toISOString(), item_count: items.length, cursor: null }));
    await tx.done;
  } catch (e) {
    warn(`saveReference(${collection})`, e);
  }
}

export interface CachedReference<T> { items: T[]; refreshed_at: string }

export async function readReference<T>(collection: string): Promise<CachedReference<T> | null> {
  const dek = getDek();
  if (!dek) return null;
  try {
    const db = await getOfflineDb();
    const scope = await readScope(dek, refScopeKey(collection));
    if (!scope) return null;
    const range = IDBKeyRange.bound(`${collection}:`, `${collection}:￿`);
    const envs = (await db.getAll('reference', range)) as Envelope[];
    const items: T[] = [];
    for (const env of envs) { try { items.push(await decryptJson<T>(dek, env)); } catch { /* skip */ } }
    return { items, refreshed_at: scope.refreshed_at };
  } catch (e) {
    warn(`readReference(${collection})`, e);
    return null;
  }
}

// ── Last-known session (offline auth tolerance) — encrypted ─────────────────────
// Profile object ONLY (id/email/name/roles/prefs) — tokens are NEVER persisted (token
// validity is unchanged; the PIN/DEK never mint server trust).

const SESSION_KEY = 'last_user';

export async function saveSessionUser(user: unknown): Promise<void> {
  const dek = getDek();
  if (!dek) return;
  try {
    const db = await getOfflineDb();
    await db.put('session', await encryptJson(dek, { user, saved_at: new Date().toISOString() }), SESSION_KEY);
  } catch (e) {
    warn('saveSessionUser', e);
  }
}

export async function readSessionUser(): Promise<unknown | null> {
  const dek = getDek();
  if (!dek) return null;
  try {
    const db = await getOfflineDb();
    const env = (await db.get('session', SESSION_KEY)) as Envelope | undefined;
    if (!env) return null;
    const rec = await decryptJson<{ user: unknown }>(dek, env);
    return rec?.user ?? null;
  } catch (e) {
    warn('readSessionUser', e);
    return null;
  }
}

/** Does an encrypted session blob exist? (existence only — no DEK / decrypt needed.) */
export async function hasSessionBlob(): Promise<boolean> {
  try { const db = await getOfflineDb(); return (await db.getKey('session', SESSION_KEY)) !== undefined; }
  catch { return false; }
}

export async function clearSessionUser(): Promise<void> {
  try {
    const db = await getOfflineDb();
    await db.delete('session', SESSION_KEY);
  } catch (e) {
    warn('clearSessionUser', e);
  }
}

// ── Persistent storage + sizing ───────────────────────────────────────────────

let persistRequested = false;

export async function requestPersistentStorage(): Promise<boolean | null> {
  if (persistRequested) return null;
  persistRequested = true;
  try {
    if (!navigator.storage?.persist) { console.info('[offline] navigator.storage.persist not available'); return null; }
    const granted = await navigator.storage.persist();
    console.info(`[offline] navigator.storage.persist() → ${granted}`);
    return granted;
  } catch (e) {
    warn('requestPersistentStorage', e);
    return null;
  }
}

export async function logStorageEstimate(label: string): Promise<void> {
  try {
    if (!navigator.storage?.estimate) return;
    const { usage, quota } = await navigator.storage.estimate();
    const mb = (n?: number) => (n == null ? '?' : (n / (1024 * 1024)).toFixed(2));
    console.info(`[offline] storage after ${label}: ${mb(usage)} MB used of ${mb(quota)} MB quota`);
  } catch (e) {
    warn('logStorageEstimate', e);
  }
}
